// src/components/RegistrationModal.jsx
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { 
  HiOutlineMail, 
  HiOutlineLockClosed, 
  HiOutlineUser, 
  HiOutlineOfficeBuilding, 
  HiOutlinePhone, 
  HiOutlineGlobe, 
  HiOutlineCalendar, 
  HiOutlineAcademicCap, 
  HiOutlineDocumentText,
  HiOutlineClock,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineArrowLeft,
  HiOutlineArrowRight
} from 'react-icons/hi';

// Status Modal Component
const RegistrationStatusModal = ({ isOpen, onClose, email }) => {
  const [status, setStatus] = useState('pending');
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    if (!isOpen || !email) return;

    const checkStatus = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/admin/status?email=${email}`
        );
        const data = await response.json();
        
        if (data.success) {
          setStatus(data.data.status);
          setRejectionReason(data.data.rejectionReason);
        }
      } catch (error) {
        console.error('Error checking status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
    
    // Poll every 10 seconds
    const interval = setInterval(checkStatus, 10000);
    return () => clearInterval(interval);
  }, [isOpen, email]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 sm:p-6 border border-gray-200">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">Registration Status</h2>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-gray-300 border-t-gray-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Checking status...</p>
          </div>
        ) : (
          <div className="text-center py-4">
            {status === 'pending' && (
              <>
                <HiOutlineClock className="w-16 h-16 sm:w-20 sm:h-20 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">Pending Approval</h3>
                <p className="text-sm sm:text-base text-gray-600">
                  Your registration has been submitted and is waiting for super admin approval.
                  You'll receive an email once approved.
                </p>
              </>
            )}

            {status === 'approved' && (
              <>
                <HiOutlineCheckCircle className="w-16 h-16 sm:w-20 sm:h-20 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">Approved!</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">
                  Your college has been approved! You can now log in.
                </p>
                <button
                  onClick={() => {
                    onClose();
                    window.location.href = '/login';
                  }}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 text-sm sm:text-base transition-colors"
                >
                  Go to Login
                </button>
              </>
            )}

            {status === 'rejected' && (
              <>
                <HiOutlineXCircle className="w-16 h-16 sm:w-20 sm:h-20 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">Registration Rejected</h3>
                <div className="bg-red-50 border border-red-200 p-3 sm:p-4 rounded-lg mb-4">
                  <p className="text-red-700 font-medium text-sm sm:text-base">Reason:</p>
                  <p className="text-red-600 text-sm sm:text-base">{rejectionReason || 'No reason provided'}</p>
                </div>
                <button
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm sm:text-base transition-colors"
                >
                  Close
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Registration Modal Component
const RegistrationModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tempId, setTempId] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Status Modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  // College Details
  const [collegeName, setCollegeName] = useState('');
  const [image, setImage] = useState('');
  const [address, setAddress] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [establishedYear, setEstablishedYear] = useState('');
  const [accreditation, setAccreditation] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminDesignation, setAdminDesignation] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [documents, setDocuments] = useState([]);

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const validateEmail = () => {
    if (!email) {
      setErrors({ email: 'Email is required' });
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setErrors({ email: 'Email is invalid' });
      return false;
    }
    return true;
  };

  const validateOTP = () => {
    if (!otp) {
      setErrors({ otp: 'OTP is required' });
      return false;
    }
    if (otp.length !== 6) {
      setErrors({ otp: 'OTP must be 6 digits' });
      return false;
    }
    return true;
  };

  const validateDetails = () => {
    const newErrors = {};
    
    if (!collegeName) newErrors.collegeName = 'College name is required';
    if (!image) newErrors.image = 'College logo/image is required';
    if (!address) newErrors.address = 'Address is required';
    if (!contactNumber) newErrors.contactNumber = 'Contact number is required';
    if (!password) newErrors.password = 'Password is required';
    if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (password && password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (!adminName) newErrors.adminName = 'Admin name is required';
    if (!adminDesignation) newErrors.adminDesignation = 'Admin designation is required';
    if (!adminPhone) newErrors.adminPhone = 'Admin phone is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOTP = async () => {
    if (!validateEmail()) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/send-otp`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      console.log(data);
      
      if (response.ok) {
        setTempId(data.data.tempId);
        setStep(2);
        toast.success('OTP sent to your email!');
      } else {
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!validateOTP()) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/verify-otp`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tempId, otp }),
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setTempId(data.data.tempId);
        setStep(3);
        toast.success('Email verified successfully!');
      } else {
        toast.error(data.message || 'Failed to verify OTP');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/resend-otp`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        toast.success('OTP resent successfully!');
      } else {
        toast.error(data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDetails = async () => {
    if (!validateDetails()) return;

    setLoading(true);
    try {
      const formData = {
        tempId,
        password,
        collegeName,
        image,
        address,
        contactNumber,
        website,
        description,
        establishedYear: parseInt(establishedYear),
        accreditation,
        adminName,
        adminDesignation,
        adminPhone,
        documents,
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/submit-details`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        toast.success('Registration submitted! Awaiting approval.');
        setRegisteredEmail(email);
        onClose();
        setShowStatusModal(true);
        
        // Reset form
        setStep(1);
        setEmail('');
        setTempId('');
        setOtp('');
        setPassword('');
        setConfirmPassword('');
        setCollegeName('');
        setImage('');
        setAddress('');
        setContactNumber('');
        setWebsite('');
        setDescription('');
        setEstablishedYear('');
        setAccreditation('');
        setAdminName('');
        setAdminDesignation('');
        setAdminPhone('');
        setDocuments([]);
      } else {
        toast.error(data.message || 'Failed to submit registration');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDocuments(prev => [...prev, {
          name: file.name,
          url: reader.result,
          type: file.type
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 p-3 sm:p-4 sticky top-0 z-10">
            <div className="flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-bold text-white">
                College Registration {step}/3
              </h2>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-300 p-1"
              >
                ✕
              </button>
            </div>
            {/* Progress Bar */}
            <div className="mt-2 h-1.5 bg-gray-600 rounded-full">
              <div 
                className="h-1.5 bg-white rounded-full transition-all duration-300"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>

          {/* Body */}
          <div className="p-4 sm:p-6">
            {/* Step 1: Email */}
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Enter Your Email</h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  We'll send a verification code to your email
                </p>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <HiOutlineMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                      placeholder="admin@college.edu"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={onClose}
                    className="w-full sm:flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm sm:text-base order-2 sm:order-1 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendOTP}
                    disabled={loading}
                    className="w-full sm:flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 text-sm sm:text-base order-1 sm:order-2 transition-colors"
                  >
                    {loading ? 'Sending...' : 'Send OTP'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Verify Your Email</h3>
                <p className="text-xs sm:text-sm text-gray-600 break-words">
                  Enter the 6-digit code sent to {email}
                </p>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    OTP Code
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-full px-3 py-2 text-lg sm:text-2xl border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 text-center tracking-widest"
                    placeholder="••••••"
                    maxLength="6"
                  />
                  {errors.otp && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.otp}</p>
                  )}
                </div>

                <div className="text-center">
                  <button
                    onClick={handleResendOTP}
                    className="text-gray-600 hover:text-gray-800 underline text-xs sm:text-sm"
                    disabled={loading}
                  >
                    Resend OTP
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    onClick={() => setStep(1)}
                    className="w-full sm:flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm sm:text-base flex items-center justify-center gap-2 transition-colors"
                  >
                    <HiOutlineArrowLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <button
                    onClick={handleVerifyOTP}
                    disabled={loading}
                    className="w-full sm:flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 text-sm sm:text-base transition-colors"
                  >
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: College Details */}
            {step === 3 && (
              <div className="space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">College Details</h3>
                
                {/* Password */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <HiOutlineLockClosed className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                      />
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.password}</p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <HiOutlineLockClosed className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                      />
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>

                {/* College Basic Info */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      College Name
                    </label>
                    <div className="relative">
                      <HiOutlineOfficeBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="text"
                        value={collegeName}
                        onChange={(e) => setCollegeName(e.target.value)}
                        className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                        placeholder="Indian School of Business"
                      />
                    </div>
                    {errors.collegeName && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.collegeName}</p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Logo/Image URL
                    </label>
                    <input
                      type="text"
                      value={image}
                      onChange={(e) => setImage(e.target.value)}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                      placeholder="https://..."
                    />
                    {errors.image && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.image}</p>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Contact Number
                    </label>
                    <div className="relative">
                      <HiOutlinePhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="text"
                        value={contactNumber}
                        onChange={(e) => setContactNumber(e.target.value)}
                        className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                        placeholder="+91 1234567890"
                      />
                    </div>
                    {errors.contactNumber && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.contactNumber}</p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <div className="relative">
                      <HiOutlineGlobe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="text"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                        placeholder="https://college.edu"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <textarea
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                    rows="3"
                    placeholder="College address"
                  />
                  {errors.address && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.address}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                    rows="3"
                    placeholder="Brief description of your college"
                  />
                </div>

                {/* Year & Accreditation */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Established Year
                    </label>
                    <div className="relative">
                      <HiOutlineCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="number"
                        value={establishedYear}
                        onChange={(e) => setEstablishedYear(e.target.value)}
                        className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                        placeholder="2001"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Accreditation
                    </label>
                    <input
                      type="text"
                      value={accreditation}
                      onChange={(e) => setAccreditation(e.target.value)}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                      placeholder="NAAC A+"
                    />
                  </div>
                </div>

                {/* Admin Details */}
                <h4 className="font-medium text-gray-700 pt-2 text-sm sm:text-base">Admin Contact</h4>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <div className="relative">
                      <HiOutlineUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="text"
                        value={adminName}
                        onChange={(e) => setAdminName(e.target.value)}
                        className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                      />
                    </div>
                    {errors.adminName && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.adminName}</p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Designation
                    </label>
                    <input
                      type="text"
                      value={adminDesignation}
                      onChange={(e) => setAdminDesignation(e.target.value)}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                      placeholder="Registrar"
                    />
                    {errors.adminDesignation && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.adminDesignation}</p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <div className="relative">
                      <HiOutlinePhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                      <input
                        type="text"
                        value={adminPhone}
                        onChange={(e) => setAdminPhone(e.target.value)}
                        className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                      />
                    </div>
                    {errors.adminPhone && (
                      <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.adminPhone}</p>
                    )}
                  </div>
                </div>

                {/* Documents */}
                {/* <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Documents (Optional)
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={handleDocumentUpload}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />
                  {documents.length > 0 && (
                    <div className="mt-2 space-y-1 max-h-20 overflow-y-auto">
                      {documents.map((doc, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                          <HiOutlineDocumentText className="text-gray-500 flex-shrink-0" />
                          <span className="truncate">{doc.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div> */}

                {/* Submit Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-6">
                  <button
                    onClick={() => setStep(2)}
                    className="w-full sm:flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm sm:text-base flex items-center justify-center gap-2 transition-colors"
                  >
                    <HiOutlineArrowLeft className="w-4 h-4" />
                    <span>Back</span>
                  </button>
                  <button
                    onClick={handleSubmitDetails}
                    disabled={loading}
                    className="w-full sm:flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 text-sm sm:text-base flex items-center justify-center gap-2 transition-colors"
                  >
                    <span>{loading ? 'Submitting...' : 'Submit Registration'}</span>
                    {!loading && <HiOutlineArrowRight className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Modal */}
      <RegistrationStatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        email={registeredEmail}
      />
    </>
  );
};

export default RegistrationModal;